import type { SupabaseClient } from '@supabase/supabase-js';
import { sendVarioMessage } from '../vario-iframe.js';
import { ensureOttoStyles, OTTO_ICON, ottoAppHead } from './otto-ui.js';

interface UninstallContext {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { tenantId: string };
}

interface UninstallResult {
  ok: boolean;
  removed_count: number;
}

/**
 * Vario opens this slot after the user clicked "Deinstallieren". Flow (mirrors the
 * @vario-software/vario-app-framework-frontend `uninstallFromErp` helper):
 *   1. Server-Cleanup (otto_uninstall RPC: Vault-Secrets + marketplace_connections-Zeile).
 *   2. `{ requestUninstall: true }` an den Vario-Host senden.
 *   3. Auf `{ uninstallState: true|false }` warten. true ⇒ Vario hat die App entfernt.
 *
 * `installationComplete` ist die Install-Erfolgsmeldung und triggert NICHT den Uninstall-Pfad.
 */
export async function renderUninstall(ctx: UninstallContext) {
  ensureOttoStyles();
  // Auto-Grow (wie Install): iframe = Inhaltshöhe, sonst deckelt bridge-§16 auf den Viewport
  // und die Foot-Buttons der Karte liegen außerhalb des sichtbaren iframe-Bereichs.
  document.documentElement.classList.add('otto-autogrow');
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = `
    <div class="otto-install">
      ${ottoAppHead('Marktplatz · Deinstallation', 'App-Deinstallation')}

      <div class="stage">
        <div class="card" id="uninstall-card">
          <div class="card-top">
            <img class="otto-badge" src="/otto-market.png" alt="OTTO Market">
            <div class="titles">
              <h2>Otto-App deinstallieren</h2>
              <div class="sub">Marktplatz-Anbindung für OTTO Market entfernen</div>
            </div>
          </div>
          <div class="done-card" id="uninstall-body">
            <div class="done-ic busy"><span class="big-spin"></span></div>
            <h2>Otto-App wird deinstalliert …</h2>
            <p id="uninstall-detail">Marktplatz-Verbindung und gespeicherte Zugangsdaten werden entfernt.</p>
          </div>
        </div>
      </div>
    </div>
  `;

  const body = ctx.contentEl.querySelector<HTMLDivElement>('#uninstall-body')!;
  const detailEl = ctx.contentEl.querySelector<HTMLParagraphElement>('#uninstall-detail')!;

  // ---- 1. Server-side teardown -------------------------------------------
  let serverNote = '';
  try {
    const { data, error } = await ctx.supabase.rpc('otto_uninstall');
    if (error) {
      serverNote = `Server-Cleanup fehlgeschlagen: ${error.message}`;
    } else {
      const r = (Array.isArray(data) ? data[0] : data) as UninstallResult | undefined;
      detailEl.textContent =
        r?.ok && (r.removed_count ?? 0) > 0
          ? 'Marktplatz-Verbindung deaktiviert, Zugangsdaten entfernt (Konfiguration bleibt erhalten).'
          : 'Keine gespeicherte Verbindung gefunden — Vario-Status wird zurückgesetzt.';
    }
  } catch (e) {
    serverNote = `Server-Cleanup-Ausnahme: ${(e as Error).message}`;
  }

  // ---- 2. Tell Vario to remove the app, wait for confirmation ------------
  const ok = await requestVarioUninstall();

  if (ok) {
    body.innerHTML = `
      <div class="done-ic">${OTTO_ICON.checkBold}</div>
      <h2>OTTO-App deinstalliert</h2>
      <p>Die Anbindung an <b>OTTO Market</b> wurde entfernt.${serverNote ? ' ' + serverNote : ''}</p>`;
  } else {
    body.innerHTML = `
      <div class="done-ic error">${OTTO_ICON.xBold}</div>
      <h2>Deinstallation abgelehnt</h2>
      <p>Vario hat die Deinstallation nicht bestätigt. Bitte versuche es später erneut oder kontaktiere den Support.${serverNote ? ' ' + serverNote : ''}</p>`;
  }
}

function requestVarioUninstall(): Promise<boolean> {
  return new Promise((resolve) => {
    const listener = (e: MessageEvent) => {
      const data = e.data as { uninstallState?: boolean } | null;
      if (!data || typeof data !== 'object' || typeof data.uninstallState !== 'boolean') return;
      window.removeEventListener('message', listener);
      resolve(data.uninstallState);
    };
    window.addEventListener('message', listener);

    // 30s safety timeout — if Vario never answers, fail closed.
    setTimeout(() => {
      window.removeEventListener('message', listener);
      resolve(false);
    }, 30_000);

    sendVarioMessage({ requestUninstall: true });
  });
}
